import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import SendIcon from "@mui/icons-material/Send";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { transformMemorableOption } from "../functions/helperFunctions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid2 as Grid,
  useTheme,
  CircularProgress,
  InputAdornment,
  Card,
  IconButton,
  Modal,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

const ChangeFA = ({
  currentUser,
  currentFA,
  setCurrentFA,
  imageList,
  setImageList,
  currentImg,
  setCurrentImg,
  imgWidth,
  setImgWidth,
  qrCodeSrc,
  mobileSize,
  tabletSize,
  smsConsent,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [selectedImage, setSelectedImage] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [inputValues, setInputValues] = useState(["", "", "", "", "", ""]);
  const [processLoad, setProcessLoad] = useState(false);
  const [blocked] = useState(false);
  const [iconsBlocked, setIconsBlocked] = useState(false);
  const [step, setStep] = useState(1); // To track the current step
  const [emailLoading, setEmailLoading] = useState(false);
  const [mobileInputDisabled, setMobileInputDisabled] = useState(false);
  const [mobileText, setMobileText] = useState("");
  const [text, setText] = useState("");
  const [showMemorable, setShowMemorable] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const [memorableValidated, setMemorableValidated] = useState(false);
  const [memoCheckLoading, setMemoCheckLoading] = useState(false);
  const [memoErrorText, setMemoErrorText] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [codeErrorText, setCodeErrorText] = useState("");
  const [showEnableScreen, setShowEnableScreen] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [memorable, setMemorable] = useState("");
  const [memorableText, setMemorableText] = useState("");
  const [memorableSelections, setMemorableSelection] = useState([]);

  const focusInput = (i) => {
    const el = document.getElementById(`input-${i}`);
    if (el) el.focus();
  };

  useEffect(() => {
    // Check if MFA is enabled for the current user
    let userMfaStatus = false;

    if (currentUser?.subuser) {
      userMfaStatus = currentUser?.subuser?.mfa ?? false;
    } else if (currentUser?.role === "notary") {
      userMfaStatus = currentUser?.mfa ?? false;
    } else {
      userMfaStatus = currentUser?.user?.mfa ?? false;
    }

    setMfaEnabled(userMfaStatus);
    setShowEnableScreen(!userMfaStatus);
  }, [currentUser]);

  useEffect(() => {
    // Fetch memorable options list
    const fetchMemorableList = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/memorable-list`,
          { withCredentials: true }
        );
        if (response.data.success) {
          setMemorableSelection(response.data.data);
        }
      } catch (e) {
        console.error("Error fetching memorable list:", e);
      }
    };

    fetchMemorableList();
  }, []);

  const initialValues = {
    mobile: "",
    code: "",
    memorableText: "",
  };

  useEffect(() => {
    if (!smsConsent && selectedImage === 1) {
      setSelectedImage(null);
    }
  }, [smsConsent, selectedImage]);

  useEffect(() => {
    if (text !== "") {
      const timer = setTimeout(() => {
        setText("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [text]);

  useEffect(() => {
    if (mobileText !== "") {
      const mobileTimer = setTimeout(() => {
        setMobileText("");
      }, 5000);

      return () => clearTimeout(mobileTimer);
    }
  }, [mobileText]);

  const handlePhoneInputChange = (event, mobileFormik) => {
    const { value } = event.target;

    const digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters
    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }
    mobileFormik.setFieldValue("mobile", formattedPhoneNumber);
  };

  const handleValidateMemorable = async (memorable, formik) => {
    try {
      setMemoCheckLoading(true);
      setShowMemorable(false);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/auth/validate-memorable-answer`,
        {
          value: memorable,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );
      if (resp.data.success) {
        setMemorableValidated(true);
      } else {
        setMemoErrorText("Security Answer Does Not Match");
        formik.setFieldValue("memorableText", "");
      }

      setMemoCheckLoading(false);
    } catch (e) {
      setMemoCheckLoading(false);
      setMemoErrorText("Security Answer Does Not Match");
      formik.setFieldValue("memorableText", "");
    }
  };

  useEffect(() => {
    const areAllFieldsFilled = inputValues.every((val) => val !== "");

    const callAuth = async () => {
      setProcessLoad(true);
      const code = inputValues.join("");

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/change-totp-auth`,
          {
            code: code,
            currentSession: currentSession,
            memorable: memorable,
            memorableText: memorableText,
          },
          { withCredentials: true }
        );
        if (response.data.success) {
          setText(response.data.message);
          setProcessLoad(false);
          setInputValues(["", "", "", "", "", ""]);
          setSelectedImage(null);
          setImgWidth(127);
          setCurrentFA("totp");
          setCurrentImg("/img/logos/authenticator.png");
          setMfaEnabled(true);
        }
      } catch (e) {
        setProcessLoad(false);
        setText(e.response.data.message);
        setInputValues(["", "", "", "", "", ""]);
      }
    };

    if (areAllFieldsFilled) {
      callAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValues, currentSession, memorable, memorableText]);

  const handleEmailFa = async () => {
    try {
      setEmailLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/change-to-emailfa`,
        {
          currentSession: currentSession,
          memorable: memorable,
          memorableText: memorableText,
        },

        { withCredentials: true }
      );

      if (response.data.success === false) {
        setEmailLoading(false);
      } else {
        setText(response.data.message);
        setSelectedImage(null);
        setEmailLoading(false);
        setImgWidth(80);
        setCurrentFA("emailFa");
        setCurrentImg("/img/logos/emailImage.png");
        setMfaEnabled(true);
      }
    } catch (e) {
      setEmailLoading(false);
    }
  };

  const handleMobileFa = async () => {
    try {
      setEmailLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/change-to-mobileFa`,
        {
          currentSession: currentSession,
          memorable: memorable,
          memorableText: memorableText,
        },

        { withCredentials: true }
      );

      if (response.data.success === false) {
        setEmailLoading(false);
      } else {
        setText(response.data.message);
        setSelectedImage(null);
        setEmailLoading(false);
        setImgWidth(140);
        setCurrentFA("smsFa");
        setCurrentImg("/img/logos/phone.png");
        setMfaEnabled(true);
      }
    } catch (e) {
      setEmailLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    try {
      setDisableLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/disable-mfa`,
        { currentSession: currentSession },
        { withCredentials: true }
      );

      if (response.data.success) {
        setText("Two-Factor Authentication has been disabled");
        setMfaEnabled(false);
        setShowEnableScreen(true);
        setCurrentFA("");
        setCurrentImg("");
        setImgWidth(0);
        setShowDisableModal(false);
        setDisableLoading(false);
      } else {
        setDisableLoading(false);
        setText(response.data.message || "Failed to disable 2FA");
      }
    } catch (e) {
      setDisableLoading(false);
      setText(e.response?.data?.message || "Error disabling 2FA");
    }
  };

  const handlePaste = (event) => {
    let text = event.clipboardData.getData("text") || "";
    // digits only
    const digits = text.replace(/\D/g, "").slice(0, 6);
    if (!digits) return;

    const filled = Array.from({ length: 6 }, (_, i) => digits[i] ?? "");
    setInputValues(filled);
    event.preventDefault();

    // focus the next empty or last box
    const nextIndex = Math.min(digits.length, 5);
    setTimeout(() => {
      focusInput(nextIndex);
    }, 0);
  };

  const handleInputChange = (index, raw) => {
    // accept only a single digit 0-9
    let v = (raw ?? "").toString().replace(/\D/g, "").slice(0, 1);

    const next = [...inputValues];
    next[index] = v;
    setInputValues(next);

    // move focus
    if (v && index < 5) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index) => (e) => {
    // block non-numeric stuff commonly allowed in number inputs
    if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
      e.preventDefault();
      return;
    }
    // backspace on empty -> move left
    if (e.key === "Backspace" && !inputValues[index] && index > 0) {
      focusInput(index - 1);
    }
    // left/right arrows to navigate
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handleImageClick = (index, formik) => {
    formik.resetForm();
    setCodeErrorText("");
    setStep(1);
    setMobileInputDisabled(false);
    setCodeSent(false);
    setMemorableValidated(false);
    setMemoErrorText("");

    if (selectedImage === index) {
      setSelectedImage(null);
    } else {
      setSelectedImage(index);
    }
  };

  const validationSchemas = [
    // Validation schema for Step 1
    Yup.object({
      mobile: Yup.string()
        .min(12, "Number is too short")
        .max(12, "Number is too long")
        .required("Mobile number is required"),
      memorableText: Yup.string()
        .min(4, "Your answer is too short")
        .required("Security question's answer is required"),
    }),
    // Validation schema for Step 2 (code)
    Yup.object({
      code: Yup.string().required("Verification code is required"),
      memorableText: Yup.string().required(
        "Security question's answer is required"
      ),
    }),
  ];

  // Generate a random six-character number

  const maskPhoneNumber = (phoneNumber) => {
    const phoneNumberString = phoneNumber.toString();

    // Mask the entire phone number, including dashes
    const maskedPhoneNumber =
      "*".repeat(phoneNumberString.length - 4) + phoneNumberString.slice(-4);

    return maskedPhoneNumber;
  };

  const mobileFormik = useFormik({
    initialValues,
    validationSchema: validationSchemas[step - 1], // Use the schema for the current step
    onSubmit: async (values, helpers) => {
      setLoading(true);
      const send_sms = async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/sms/add-mobile-send-code`,
            {
              mobile: values["mobile"],
              memorable: values["memorableText"],
              currentSession: currentSession,
            },
            { withCredentials: true }
          );
          setLoading(false);
          return resp;
        } catch (e) {
          setMobileText(e.response.data.message);
          helpers.setFieldValue("memorableText", "");
          setLoading(false);
          return;
        }
      };

      if (step === 1) {
        const send_response = await send_sms();

        if (send_response?.data?.success === true) {
          setStep(2);
          setMobileInputDisabled(true);
          setMobileText(send_response?.data?.message);
          setIconsBlocked(true);
          setLoading(false);
          setCodeSent(true);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(true);
        const verify_sms = async () => {
          try {
            const resp = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/confirm-change-mobile`,
              {
                code: values["code"],
                memorableText: values["memorableText"],
                currentSession: currentSession,
              },
              { withCredentials: true }
            );
            setLoading(false);
            setText("Mobile verification has been set successfully");
            setSelectedImage(null);
            setEmailLoading(false);
            setImgWidth(140);
            setCurrentFA("smsFa");
            setCurrentImg("/img/logos/phone.png");
            setIconsBlocked(false);
            setMfaEnabled(true);
            return resp;
          } catch (e) {
            helpers.setFieldValue("code", "");
            setLoading(false);
            setCodeErrorText("Invalid Verification code");
            setIconsBlocked(false);
          }
        };

        const smsResp = await verify_sms();

        if (smsResp?.data?.success) {
        } else {
          helpers.setFieldValue("code", "");
          setLoading(false);
          setCodeErrorText("Invalid Verification code");
        }
      }
    },
  });

  return (
    <>
      <Card
        pb={3}
        pt={1}
        px={mobileSize ? 1 : 3}
        mb={4}
        component={Grid}
        sx={{
          width: "100%",
          maxWidth: mobileSize ? "100%" : tabletSize ? "90vw" : "60vw",
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        <Grid size={{ xs: 12 }} mt={1}>
          <Typography
            sx={{ textAlign: "left", mb: mobileSize ? 2 : 5 }}
            variant="h5"
          >
            Two-Factor Authentication
          </Typography>

          {showEnableScreen && !mfaEnabled ? (
            <>
              <Box
                sx={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "15px",
                  p: mobileSize ? 2 : 4,
                  mb: 3,
                  mt: 2,
                  border: "2px solid #e0e0e0",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Typography
                  color="text.primary"
                  variant={mobileSize ? "h6" : "h5"}
                  align="center"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  Enhance Your Account Security
                </Typography>

                <Typography
                  color="text.secondary"
                  variant={mobileSize ? "body2" : "body1"}
                  align="center"
                  sx={{ mb: 3 }}
                >
                  Two-Factor Authentication (2FA) adds an extra layer of
                  security to your account. Enable it now to protect your
                  sensitive data and projects.
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    listStyle: "none",
                    p: 0,
                    m: 0,
                    mb: 3,
                  }}
                >
                  <Box
                    component="li"
                    sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
                  >
                    <CheckCircleIcon
                      color="success"
                      sx={{ mr: 1, fontSize: mobileSize ? 20 : 24 }}
                    />
                    <Typography
                      variant={mobileSize ? "body2" : "body1"}
                      color="text.secondary"
                    >
                      Prevents unauthorized access even if your password is
                      compromised
                    </Typography>
                  </Box>
                  <Box
                    component="li"
                    sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
                  >
                    <CheckCircleIcon
                      color="success"
                      sx={{ mr: 1, fontSize: mobileSize ? 20 : 24 }}
                    />
                    <Typography
                      variant={mobileSize ? "body2" : "body1"}
                      color="text.secondary"
                    >
                      Protects your sensitive project data and documents
                    </Typography>
                  </Box>
                  <Box
                    component="li"
                    sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
                  >
                    <CheckCircleIcon
                      color="success"
                      sx={{ mr: 1, fontSize: mobileSize ? 20 : 24 }}
                    />
                    <Typography
                      variant={mobileSize ? "body2" : "body1"}
                      color="text.secondary"
                    >
                      Choose from multiple authentication methods
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography
                  variant={mobileSize ? "body2" : "body1"}
                  color="text.primary"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  Security Question
                </Typography>

                <Typography
                  variant={mobileSize ? "body2" : "body1"}
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This will help you recover access if you lose your 2FA device.
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }} variant="filled">
                  <InputLabel id="memorable-select-label">
                    Select a Security Question
                  </InputLabel>
                  <Select
                    id="memorable-select"
                    value={memorable}
                    onChange={(e) => setMemorable(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          marginTop: "1.5px",
                          maxHeight: 150, // Set the desired max height for the dropdown menu
                          width: "auto",
                          maxWidth: "100%",
                        },
                      },
                    }}
                  >
                    {memorableSelections?.map(function (item, i) {
                      return (
                        <MenuItem key={i} value={item}>
                          {transformMemorableOption(item)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Your Answer"
                  value={memorableText}
                  onChange={(e) => setMemorableText(e.target.value)}
                  placeholder="Enter your answer"
                  sx={{ mb: 3 }}
                />

                <Button
                  fullWidth
                  size="normal"
                  variant="contained"
                  color="primary"
                  disabled={!memorable || !memorableText}
                  onClick={() => setShowEnableScreen(false)}
                  sx={{
                    py: 1.5,

                    fontWeight: "bold",
                    borderRadius: "10px",
                    textTransform: "none",
                  }}
                >
                  Enable 2FA Now
                </Button>
              </Box>
            </>
          ) : null}
        </Grid>

        {!showEnableScreen && (
          <Grid container alignItems="center" sx={{ width: "100%", mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              {mfaEnabled && (
                <Typography align="left" variant="h6">
                  Current 2FA
                </Typography>
              )}
            </Grid>
            <Grid
              size={{ xs: 6 }}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="outlined"
                color={mfaEnabled ? "error" : "warning"}
                size="medium"
                onClick={() => {
                  if (mfaEnabled) {
                    setShowDisableModal(true);
                  } else {
                    setShowEnableScreen(true);
                    setSelectedImage(null);
                  }
                }}
                sx={{ textTransform: "none" }}
              >
                {mfaEnabled ? "Disable 2FA" : "Cancel"}
              </Button>
            </Grid>
          </Grid>
        )}

        {!showEnableScreen && (
          <>
            <Grid container alignItems="center" sx={{ width: "100%" }}>
              <Grid size={{ xs: 12 }} textAlign="center" sx={{ width: "100%" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img alt={currentFA} src={currentImg} width={imgWidth} />
                </Box>
              </Grid>
            </Grid>
            <div
              style={{
                border: "4px solid #f4f4f4",
                marginTop: "1rem",
                marginBottom: "1rem",
              }}
            />
          </>
        )}

        <Typography align="center" sx={{ mb: "2rem" }} variant="h6">
          {!showEnableScreen && mfaEnabled
            ? "Change Two-Factor Authentication"
            : showEnableScreen
              ? ""
              : "Select Your 2FA Method"}
        </Typography>

        {!showEnableScreen && (
          <Grid container justifyContent="center" sx={{ width: "100%" }}>
            {imageList?.map(
              (image, index) =>
                (mfaEnabled ? image.src !== currentImg : true) && (
                  <Grid
                    size={{ xs: 6, lg: 4 }}
                    key={index}
                    sx={{ width: "100%" }}
                  >
                    <Box
                      sx={
                        blocked ||
                        iconsBlocked ||
                        (image?.alt === "mobile" &&
                          (process.env.REACT_APP_DISABLE_SMS_FA === "true" ||
                            !smsConsent))
                          ? {
                              borderRadius: "10px",
                              backgroundColor: "transparent",
                              flexDirection: "column",
                              cursor: "auto",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%",
                            }
                          : {
                              borderRadius: "10px",
                              backgroundColor:
                                selectedImage === index
                                  ? "#f3f3f3"
                                  : "transparent",
                              flexDirection: "column",
                              cursor: "pointer",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%",
                              transform:
                                hovered === index ? "scale(1.1)" : "scale(1)",
                              transition: "transform 0.3s ease",
                            }
                      }
                      onClick={() =>
                        iconsBlocked ||
                        (image?.alt === "mobile" &&
                          (process.env.REACT_APP_DISABLE_SMS_FA === "true" ||
                            !smsConsent))
                          ? null
                          : handleImageClick(index, mobileFormik)
                      }
                      onMouseEnter={() =>
                        !iconsBlocked ? setHovered(index) : null
                      }
                      // onMouseLeave={handleImageLeave}
                    >
                      <Box sx={{ pb: image.alt === "mobile" ? 3.9 : 3, pt: 1 }}>
                        <img
                          alt={image.alt}
                          src={image.src}
                          width={
                            selectedImage === index
                              ? image.width * 1.1
                              : image.width
                          }
                        />
                      </Box>
                      {image?.alt === "mobile" &&
                      process.env.REACT_APP_DISABLE_SMS_FA === "true" ? (
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="error"
                          align="center"
                          px={2}
                          mt={-3}
                        >
                          SMS-based 2FA verification is temporarily unavailable
                        </Typography>
                      ) : (
                        !smsConsent &&
                        image?.alt === "mobile" && (
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="error"
                            align="center"
                            px={2}
                            mt={-3}
                          >
                            SMS-based 2FA verification is not enabled
                          </Typography>
                        )
                      )}
                    </Box>
                  </Grid>
                )
            )}
            {selectedImage === 0 && currentFA !== "emailFa" && (
              <>
                <Grid size={{ xs: 12 }} sx={{ mt: 5 }}>
                  <LoadingButton
                    // SUBMIT //
                    onClick={handleEmailFa}
                    loading={emailLoading}
                    fullWidth
                    size="medium"
                    sx={{ mt: 0, p: 1.5 }}
                    variant="contained"
                  >
                    Save
                  </LoadingButton>
                </Grid>
              </>
            )}
            {selectedImage === 1 && currentFA !== "smsFa" && smsConsent && (
              <>
                <Grid container spacing={0}>
                  {currentUser?.subuser ? (
                    <>
                      {" "}
                      {currentUser?.subuser?.auth_mobile &&
                      !currentUser?.subuser?.mobile_code ? (
                        <>
                          <Grid sx={{ mt: 6 }} size={{ sm: 12 }}>
                            <Typography
                              align="center"
                              sx={{ mb: "2rem" }}
                              variant="h6"
                            >
                              Phone Number
                            </Typography>
                            <Typography
                              align="center"
                              sx={{ mb: "2rem" }}
                              variant="h5"
                            >
                              {maskPhoneNumber(
                                currentUser?.subuser.auth_mobile
                              )}
                            </Typography>
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid sx={{ mt: 6 }} size={{ xs: 12 }}>
                            <Typography
                              align="left"
                              sx={{ mb: "2rem" }}
                              variant="h6"
                            >
                              ADD Phone Number
                            </Typography>
                          </Grid>
                          <Grid size={{ sm: 12 }}>
                            <Typography
                              variant="h6"
                              align="center"
                              sx={{ mb: 2 }}
                            >
                              {currentUser?.subuser.memorable_option}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12 }} align="center">
                            <TextField
                              size={mobileSize ? "small" : undefined}
                              error={
                                !!(
                                  mobileFormik.touched.memorableText &&
                                  mobileFormik.errors.memorableText
                                )
                              }
                              helperText={
                                mobileFormik.touched.memorableText &&
                                mobileFormik.errors.memorableText
                              }
                              name="memorableText"
                              disabled={Boolean(mobileInputDisabled)}
                              type={showMemorable ? "text" : "password"}
                              label="Enter your answer here"
                              value={mobileFormik.values.memorableText}
                              onChange={(e) => mobileFormik.handleChange(e)}
                              onBlur={mobileFormik.handleBlur}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => {
                                        setShowMemorable(!showMemorable);
                                      }}
                                      onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                                      onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                      edge="end"
                                    >
                                      {showMemorable ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <form
                            style={{ width: "100%" }}
                            noValidate
                            autoComplete="off"
                            onSubmit={mobileFormik.handleSubmit}
                          >
                            <Grid container spacing={2} sx={{ width: "100%" }}>
                              <Grid
                                sx={{ mt: 6, mb: 6 }}
                                size={{ xs: 12, lg: 6 }}
                              >
                                <TextField
                                  size={mobileSize ? "small" : undefined}
                                  error={
                                    !!(
                                      mobileFormik.touched.mobile &&
                                      mobileFormik.errors.mobile
                                    )
                                  }
                                  helperText={
                                    mobileFormik.touched.mobile &&
                                    mobileFormik.errors.mobile
                                  }
                                  onKeyUp={(e) =>
                                    handlePhoneInputChange(e, mobileFormik)
                                  }
                                  disabled={Boolean(mobileInputDisabled)}
                                  name="mobile"
                                  label="Mobile Number"
                                  variant="filled"
                                  fullWidth
                                  onBlur={mobileFormik.handleBlur}
                                  value={mobileFormik.values.mobile}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        +1
                                      </InputAdornment>
                                    ),
                                    inputProps: { pattern: "[0-9]*" }, // Allow only numeric input
                                    // Add any other input props or attributes as needed
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  onChange={(e) => {
                                    if (e.target.value.length > 12) {
                                      return;
                                    } else {
                                      mobileFormik.handleChange(e);
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid sx={{ mt: 6 }} size={{ sm: 12, lg: 6 }}>
                                {step === 2 && (
                                  <TextField
                                    size={mobileSize ? "small" : undefined}
                                    error={
                                      !!(
                                        mobileFormik.touched.code &&
                                        mobileFormik.errors.code
                                      )
                                    }
                                    fullWidth
                                    helperText={
                                      mobileFormik.touched.code &&
                                      mobileFormik.errors.code
                                    }
                                    name="code"
                                    value={mobileFormik.values.code}
                                    label="Verification Code"
                                    variant="filled"
                                    onChange={(e) => {
                                      mobileFormik.handleChange(e); // Trigger Formik's handleChange as well
                                    }}
                                  />
                                )}
                              </Grid>
                            </Grid>
                            <Grid size={{ sm: 12 }}>
                              <Typography
                                variant="body1"
                                sx={{ minHeight: "1.5rem" }}
                                textAlign="center"
                                my={1}
                                color="error"
                                fontWeight="bold"
                              >
                                {mobileText}
                              </Typography>
                            </Grid>
                            <Grid size={{ sm: 12 }}>
                              <LoadingButton
                                loading={loading}
                                fullWidth
                                size="large"
                                sx={{ mt: 0, p: 2 }}
                                type="submit"
                                variant="contained"
                              >
                                {step === 1
                                  ? "Send Verification Code"
                                  : "S U B M I T"}
                              </LoadingButton>
                            </Grid>
                          </form>
                        </>
                      )}
                    </>
                  ) : currentUser?.role === "notary" ? (
                    <>
                      {currentUser?.auth_mobile && !currentUser?.mobile_code ? (
                        <>
                          <Grid sx={{ mt: 6 }} size={{ sm: 12 }}>
                            <Typography
                              align="center"
                              sx={{ mb: "2rem" }}
                              variant="h6"
                            >
                              Phone Number
                            </Typography>
                            <Typography
                              align="center"
                              sx={{ mb: "2rem" }}
                              variant="h5"
                            >
                              {maskPhoneNumber(currentUser?.auth_mobile)}
                            </Typography>
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid sx={{ mt: 6 }} size={{ xs: 12 }}>
                            <Typography
                              align="left"
                              sx={{ mb: "2rem" }}
                              variant="h6"
                            >
                              ADD Phone Number
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <Typography
                              variant="h6"
                              align="center"
                              sx={{ mb: 2 }}
                            >
                              {currentUser?.memorable_option}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12 }} align="center">
                            <TextField
                              size={mobileSize ? "small" : undefined}
                              error={
                                !!(
                                  mobileFormik.touched.memorableText &&
                                  mobileFormik.errors.memorableText
                                )
                              }
                              helperText={
                                mobileFormik.touched.memorableText &&
                                mobileFormik.errors.memorableText
                              }
                              name="memorableText"
                              disabled={Boolean(mobileInputDisabled)}
                              type={showMemorable ? "text" : "password"}
                              label="Enter your answer here"
                              value={mobileFormik.values.memorableText}
                              onChange={(e) => mobileFormik.handleChange(e)}
                              onBlur={mobileFormik.handleBlur}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => {
                                        setShowMemorable(!showMemorable);
                                      }}
                                      onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                                      onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                      edge="end"
                                    >
                                      {showMemorable ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <form
                            style={{ width: "100%" }}
                            noValidate
                            autoComplete="off"
                            onSubmit={mobileFormik.handleSubmit}
                          >
                            <Grid container spacing={2} sx={{ width: "100%" }}>
                              <Grid
                                sx={{ mt: 6, mb: 6 }}
                                size={{ xs: 12, lg: 6 }}
                              >
                                <TextField
                                  size={mobileSize ? "small" : undefined}
                                  error={
                                    !!(
                                      mobileFormik.touched.mobile &&
                                      mobileFormik.errors.mobile
                                    )
                                  }
                                  helperText={
                                    mobileFormik.touched.mobile &&
                                    mobileFormik.errors.mobile
                                  }
                                  onKeyUp={(e) =>
                                    handlePhoneInputChange(e, mobileFormik)
                                  }
                                  disabled={Boolean(mobileInputDisabled)}
                                  name="mobile"
                                  label="Mobile Number"
                                  variant="filled"
                                  fullWidth
                                  onBlur={mobileFormik.handleBlur}
                                  value={mobileFormik.values.mobile}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        +1
                                      </InputAdornment>
                                    ),
                                    inputProps: { pattern: "[0-9]*" }, // Allow only numeric input
                                    // Add any other input props or attributes as needed
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                  onChange={(e) => {
                                    if (e.target.value.length > 12) {
                                      return;
                                    } else {
                                      mobileFormik.handleChange(e);
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid sx={{ mt: 6 }} size={{ xs: 12, lg: 6 }}>
                                {step === 2 && (
                                  <TextField
                                    size={mobileSize ? "small" : undefined}
                                    error={
                                      !!(
                                        mobileFormik.touched.code &&
                                        mobileFormik.errors.code
                                      )
                                    }
                                    fullWidth
                                    helperText={
                                      mobileFormik.touched.code &&
                                      mobileFormik.errors.code
                                    }
                                    name="code"
                                    value={mobileFormik.values.code}
                                    label="Verification Code"
                                    variant="filled"
                                    onChange={(e) => {
                                      mobileFormik.handleChange(e); // Trigger Formik's handleChange as well
                                    }}
                                  />
                                )}
                              </Grid>
                            </Grid>
                            <Grid size={{ sm: 12 }}>
                              <Typography
                                variant="body1"
                                sx={{ minHeight: "1.5rem" }}
                                textAlign="center"
                                my={1}
                                color="error"
                                fontWeight="bold"
                              >
                                {mobileText}
                              </Typography>
                            </Grid>
                            <Grid size={{ sm: 12 }}>
                              <LoadingButton
                                loading={loading}
                                fullWidth
                                size={mobileSize ? "small" : "large"}
                                sx={{ mt: 0, p: 2 }}
                                type="submit"
                                variant="contained"
                              >
                                {step === 1
                                  ? "Send Verification Code"
                                  : "S U B M I T"}
                              </LoadingButton>
                            </Grid>
                          </form>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {currentUser?.user?.auth_mobile &&
                      !currentUser?.user?.mobile_code ? (
                        <>
                          <Grid sx={{ mt: 6 }} size={{ xs: 12 }}>
                            <Typography
                              align="center"
                              sx={{ mb: "2rem" }}
                              variant="h6"
                            >
                              Phone Number
                            </Typography>
                            <Typography
                              align="center"
                              sx={{ mb: "2rem" }}
                              variant="h5"
                            >
                              {maskPhoneNumber(currentUser?.user.auth_mobile)}
                            </Typography>
                          </Grid>
                        </>
                      ) : (
                        // What is.... + your ? --> A(replace) | Favorite | Birthplace |
                        <>
                          <Box
                            sx={{
                              border: "2px solid #d3d3d3",
                              mt: 2,
                              pb: 3,
                              borderRadius: "10px",
                              boxShadow: "2px 3px 6px 3px rgba(0, 0, 0, 0.2)",
                              px: 2,
                              width: "100%",
                              maxWidth: "100%",
                              boxSizing: "border-box",
                              overflow: "hidden",
                            }}
                          >
                            <Grid
                              sx={{ mt: mobileSize ? 1 : 3 }}
                              size={{ xs: 12 }}
                            >
                              <Typography
                                sx={{
                                  mt: 0,
                                  fontWeight: mobileSize ? "bold" : undefined,
                                }}
                                color="primary"
                                variant={mobileSize ? "body2" : "h6"}
                                align="center"
                              >
                                If you would like to set up 2FA with text
                                message, you must enter your security question's
                                answer and confirm your phone number
                              </Typography>
                            </Grid>
                            <Grid
                              container
                              alignItems="center"
                              spacing={1}
                              sx={{ width: "100%" }}
                            >
                              <Grid
                                size={{ xs: 12 }}
                                sx={{ mt: mobileSize ? 2 : 4 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold", fontSize: "15px" }}
                                >
                                  {currentUser?.user.memorable_option
                                    ? transformMemorableOption(
                                        currentUser.user.memorable_option
                                      )
                                    : ""}
                                </Typography>
                              </Grid>

                              <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  size={mobileSize ? "small" : undefined}
                                  error={
                                    !!(
                                      mobileFormik.touched.memorableText &&
                                      mobileFormik.errors.memorableText
                                    )
                                  }
                                  helperText={
                                    mobileFormik.touched.memorableText &&
                                    mobileFormik.errors.memorableText
                                  }
                                  name="memorableText"
                                  fullWidth
                                  disabled={
                                    Boolean(mobileInputDisabled) ||
                                    Boolean(memoCheckLoading) ||
                                    Boolean(memorableValidated)
                                  }
                                  type={showMemorable ? "text" : "password"}
                                  label="Enter the answer to your security question"
                                  value={mobileFormik.values.memorableText}
                                  onChange={(e) => {
                                    mobileFormik.handleChange(e);
                                    memoErrorText && setMemoErrorText("");
                                  }}
                                  onBlur={mobileFormik.handleBlur}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() => {
                                            setShowMemorable(!showMemorable);
                                          }}
                                          onMouseDown={(e) =>
                                            e.preventDefault()
                                          } // Prevent focus change on mouse down
                                          onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                          edge="end"
                                        >
                                          {showMemorable ? (
                                            <Visibility />
                                          ) : (
                                            <VisibilityOff />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <LoadingButton
                                  size={mobileSize ? "small" : undefined}
                                  fullWidth
                                  onClick={() => {
                                    handleValidateMemorable(
                                      mobileFormik?.values?.memorableText,
                                      mobileFormik
                                    );
                                    mobileFormik.setErrors({});
                                  }}
                                  sx={{
                                    mb:
                                      mobileFormik.errors.memorableText &&
                                      mobileFormik.touched.memorableText
                                        ? "24px"
                                        : "0px",
                                    ...(memorableValidated && {
                                      color: theme.palette.success.contrastText, // Text color
                                      backgroundColor:
                                        theme.palette.success.main, // Background color
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.success.dark, // Darken the color a bit on hover
                                      },
                                      // Custom disabled styles
                                      "&.Mui-disabled": {
                                        color:
                                          theme.palette.success.contrastText, // Keeps text color on disabled state
                                        backgroundColor:
                                          theme.palette.success.light, // Lighter shade for disabled background
                                      },
                                    }),
                                  }}
                                  loading={memoCheckLoading}
                                  variant="contained"
                                  disabled={
                                    Boolean(
                                      mobileFormik?.values?.memorableText
                                        ?.length < 4
                                    ) ||
                                    Boolean(
                                      mobileFormik.errors.memorableText
                                    ) ||
                                    Boolean(
                                      !mobileFormik?.values?.memorableText
                                    ) ||
                                    Boolean(memorableValidated)
                                  }
                                  color="warning" // This color will be overridden when memorableValidated is true
                                  endIcon={
                                    memorableValidated ? (
                                      <CheckCircleIcon />
                                    ) : (
                                      <SendIcon />
                                    )
                                  }
                                >
                                  {memorableValidated
                                    ? "Verified"
                                    : "Validate Answer"}
                                </LoadingButton>
                              </Grid>
                              <Typography
                                variant={mobileSize ? "body2" : "h6"}
                                mx="auto"
                                my={1}
                                color="error"
                                fontWeight="bold"
                              >
                                {memoErrorText}
                              </Typography>
                            </Grid>
                            <form
                              style={{ width: "100%" }}
                              noValidate
                              autoComplete="off"
                              onSubmit={mobileFormik.handleSubmit}
                            >
                              {memorableValidated && (
                                <>
                                  <Grid
                                    container
                                    spacing={0}
                                    sx={{ width: "100%" }}
                                  >
                                    <Grid
                                      size={{ sm: 12 }}
                                      sx={{ mt: mobileSize ? 2 : 4, mb: 2 }}
                                    >
                                      <Typography
                                        sx={{ mt: 0 }}
                                        color="primary"
                                        variant="body2"
                                        fontWeight="bold"
                                        align="center"
                                      >
                                        Enter your phone number, then use the
                                        code that is sent by text message to
                                        complete the process and change your 2FA
                                        method.
                                      </Typography>
                                    </Grid>
                                    <Grid
                                      sx={{ mb: 0 }}
                                      size={{ xs: 12, sm: 4 }}
                                    >
                                      <TextField
                                        size={mobileSize ? "small" : undefined}
                                        error={
                                          !!(
                                            mobileFormik.touched.mobile &&
                                            mobileFormik.errors.mobile
                                          )
                                        }
                                        helperText={
                                          mobileFormik.touched.mobile &&
                                          mobileFormik.errors.mobile
                                        }
                                        disabled={Boolean(mobileInputDisabled)}
                                        name="mobile"
                                        onKeyUp={(e) =>
                                          handlePhoneInputChange(
                                            e,
                                            mobileFormik
                                          )
                                        }
                                        label="Mobile Number"
                                        variant="filled"
                                        fullWidth
                                        onBlur={mobileFormik.handleBlur}
                                        value={mobileFormik.values.mobile}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              +1
                                            </InputAdornment>
                                          ),
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => {
                                          if (e.target.value.length > 12) {
                                            return;
                                          } else {
                                            mobileFormik.handleChange(e);
                                          }
                                        }}
                                      />
                                    </Grid>
                                    <Grid
                                      size={{ xs: 12, sm: 4 }}
                                      mt={mobileFormik ? 1 : undefined}
                                      mb={
                                        (mobileFormik.errors.mobile &&
                                          mobileFormik.touched.mobile) ||
                                        (mobileFormik.errors.code &&
                                          mobileFormik.touched.code)
                                          ? "22px"
                                          : "0px"
                                      }
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {mobileFormik.values.code.length < 6 ? (
                                        <>
                                          <LoadingButton
                                            sx={{
                                              mb: mobileSize ? 1 : undefined,
                                            }}
                                            fullWidth={mobileSize}
                                            loading={loading}
                                            disabled={
                                              Boolean(
                                                mobileFormik.values.mobile
                                                  .length !== 12
                                              ) || Boolean(codeSent)
                                            }
                                            variant="contained"
                                            type="submit"
                                            color="warning"
                                            endIcon={<SendIcon />}
                                          >
                                            {codeSent
                                              ? "Verification Code Sent"
                                              : "Send Verification Code"}
                                          </LoadingButton>
                                        </>
                                      ) : (
                                        <>
                                          <LoadingButton
                                            sx={{
                                              mb: mobileSize ? 1 : undefined,
                                            }}
                                            size={
                                              mobileSize ? "small" : undefined
                                            }
                                            loading={loading}
                                            variant="contained"
                                            type="submit"
                                            color="primary"
                                            endIcon={<AssignmentTurnedInIcon />}
                                          >
                                            Complete Verification
                                          </LoadingButton>
                                        </>
                                      )}
                                    </Grid>
                                    <Grid
                                      sx={{ mt: 0 }}
                                      size={{ xs: 12, sm: 4 }}
                                    >
                                      {step === 2 && (
                                        <TextField
                                          size={
                                            mobileSize ? "small" : undefined
                                          }
                                          error={
                                            !!(
                                              mobileFormik.touched.code &&
                                              mobileFormik.errors.code
                                            )
                                          }
                                          fullWidth
                                          helperText={
                                            mobileFormik.touched.code &&
                                            mobileFormik.errors.code
                                          }
                                          name="code"
                                          value={mobileFormik.values.code}
                                          label="Verification Code"
                                          variant="filled"
                                          onChange={(e) => {
                                            mobileFormik.handleChange(e);
                                            codeErrorText &&
                                              setCodeErrorText("");
                                          }}
                                        />
                                      )}
                                    </Grid>
                                  </Grid>
                                  <Typography
                                    variant="body1"
                                    sx={{ minHeight: "1.5rem" }}
                                    align="center"
                                    my={1}
                                    color="error"
                                    fontWeight="bold"
                                  >
                                    {codeErrorText}
                                  </Typography>
                                </>
                              )}
                            </form>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Grid>
                {currentUser?.subuser ? (
                  <>
                    {currentUser?.subuser?.auth_mobile &&
                      !currentUser?.subuser?.mobile_code && (
                        <Grid size={{ xs: 12 }}>
                          <LoadingButton
                            size="medium"
                            onClick={handleMobileFa}
                            loading={emailLoading}
                            fullWidth
                            sx={{ mt: 0, p: 1.5 }}
                            type="submit"
                            variant="contained"
                          >
                            Save
                          </LoadingButton>
                        </Grid>
                      )}
                  </>
                ) : (
                  <>
                    {currentUser?.user?.auth_mobile &&
                      !currentUser?.user?.mobile_code && (
                        <Grid size={{ xs: 12 }}>
                          <LoadingButton
                            onClick={handleMobileFa}
                            loading={emailLoading}
                            fullWidth
                            size="medium"
                            sx={{ mt: 0, p: 1.5 }}
                            type="submit"
                            variant="contained"
                          >
                            Save
                          </LoadingButton>
                        </Grid>
                      )}
                  </>
                )}
              </>
            )}
            {selectedImage === 2 && currentFA !== "totp" && (
              <>
                <Grid size={{ xs: 12 }} align="center" sx={{ mt: 4 }}>
                  <Typography
                    variant={mobileSize ? "h6" : "h5"}
                    color="primary"
                    fontWeight="bold"
                  >
                    Please input the verification code to select this 2FA
                    Authentication method.
                  </Typography>
                  <Typography
                    sx={{ mt: mobileSize ? 1 : 2 }}
                    variant="body2"
                    color="primary"
                    fontWeight="bold"
                  >
                    If you've misplaced the QR code or are scanning it for the
                    first time, feel free to scan it again.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }} align="center" sx={{ mt: 2 }}>
                  <Button
                    size={mobileSize ? "small" : undefined}
                    variant="contained"
                    onClick={() => {
                      setShowQr(!showQr);
                    }}
                    startIcon={
                      <>
                        {showQr ? (
                          <VisibilityOff
                            size="small"
                            sx={{ color: "inherit" }}
                          />
                        ) : (
                          <Visibility />
                        )}
                      </>
                    }
                  >
                    {showQr ? "Hide QR Code" : "Show QR Code"}
                  </Button>
                </Grid>

                {showQr && (
                  <Grid size={{ xs: 12 }}>
                    <Box
                      display="flex"
                      justifyContent="center" // Center horizontally
                      alignItems="center" // Center vertically
                      height="100%" // Ensure the box takes the full height
                    >
                      {qrCodeSrc ? (
                        <img
                          width={mobileSize ? 150 : 200}
                          src={qrCodeSrc}
                          alt="QR Code"
                        />
                      ) : (
                        <p>Loading QR Code...</p>
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid container sx={{ width: "100%" }}>
                  <Grid size={{ xs: 12 }} sx={{ mx: "auto", width: "100%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        ml: "auto",
                        mr: "auto",
                        mt: 2,
                        width: "100%",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      {processLoad ? (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              width: "100%",
                              height: "100%",
                              justifyContent: "center",
                              alignItems: "center",
                              flexDirection: "column",
                            }}
                          >
                            <CircularProgress color="warning" size="5rem" />
                          </Box>
                        </>
                      ) : (
                        inputValues.map((value, index) => (
                          <Grid
                            size={{ xs: 12 }}
                            key={index}
                            sx={{
                              mx: mobileSize ? "2px" : "6px",
                              width: "auto",
                              flex: "0 0 auto",
                            }}
                          >
                            <TextField
                              size={mobileSize ? "small" : undefined}
                              disabled={Boolean(blocked)}
                              id={`input-${index}`}
                              value={inputValues[index]}
                              // Mobile-friendly + keypad:
                              type="tel"
                              inputMode="numeric"
                              variant="outlined"
                              onChange={(e) =>
                                handleInputChange(index, e.target.value)
                              }
                              onKeyDown={handleKeyDown(index)}
                              onPaste={index === 0 ? handlePaste : undefined}
                              slotProps={{
                                htmlInput: {
                                  maxLength: 1, // still helpful as a UI hint
                                  pattern: "[0-9]*", // hint for some browsers/keyboards
                                  style: {
                                    fontSize: mobileSize ? "20px" : "28px",
                                    fontWeight: "bold",
                                    width: "100%",
                                    // Firefox spinner guard if you ever switch back to type="number"
                                    MozAppearance: "textfield",
                                  },
                                },
                              }}
                              sx={{
                                width: mobileSize ? "40px" : "48px",
                                textAlign: "center",
                                border: "3px solid #000",
                                borderRadius: "10px",

                                // Hide number spinners in WebKit (in case type="number" is used)
                                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                  {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                              }}
                            />
                          </Grid>
                        ))
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        )}
        <Typography
          mt={2}
          variant="body1"
          color={text.includes("success") ? "success.main" : "error"}
          fontWeight="bold"
          align="center"
        >
          {text}
        </Typography>
      </Card>

      {/* Disable 2FA Confirmation Modal */}
      <Modal
        open={showDisableModal}
        onClose={() => !disableLoading && setShowDisableModal(false)}
        aria-labelledby="disable-2fa-modal"
        aria-describedby="disable-2fa-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: mobileSize ? "90%" : tabletSize ? "70%" : "500px",
            bgcolor: "background.paper",
            borderRadius: "15px",
            boxShadow: 24,
            p: mobileSize ? 2 : 4,
          }}
        >
          <Typography
            id="disable-2fa-modal"
            variant={mobileSize ? "h6" : "h5"}
            component="h2"
            sx={{ mb: 2, fontWeight: "bold", color: "error.main" }}
          >
            Disable Two-Factor Authentication?
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography
            id="disable-2fa-description"
            variant="body1"
            sx={{ mb: 1 }}
          >
            Are you sure you want to disable Two-Factor Authentication?
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <strong>Warning:</strong> Disabling 2FA will make your account less
            secure. Without 2FA, anyone who obtains your password will be able
            to access your account. We strongly recommend keeping 2FA enabled to
            protect your sensitive data and projects.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={() => setShowDisableModal(false)}
              disabled={disableLoading}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleDisable2FA}
              loading={disableLoading}
              sx={{ textTransform: "none" }}
            >
              Yes, Disable 2FA
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ChangeFA;
